import { getAmplifyCookieStorageOptions } from '@/utils/auth/cookieSecurity';
import { getAWSEnvironment, getAWSRegion } from '@/utils/aws/environment';
import { IS_ETS_NEXT, IS_LOCALHOST } from '@/utils/constants';
import { Amplify } from 'aws-amplify';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';

const DEFAULT_AUTH_FLOW_TYPE = 'CUSTOM_AUTH';

let activeAuthFlowType = DEFAULT_AUTH_FLOW_TYPE;
let lastAmplifyConfigKey = '';
let lastCookieStorageKey = '';

export function getCognitoConfig() {
  const awsEnvironment = getAWSEnvironment();
  const awsRegion = getAWSRegion();

  if (awsEnvironment === 'sso') {
    return {
      region: awsRegion,
      poolId: 'us-east-1_GDcHEV6qU',
      clientId: '5g8ua5puqimf66lbk15ju7f9ne',
    };
  } else if (awsEnvironment === 'dev') {
    return {
      region: awsRegion,
      poolId: 'us-east-2_4YQ99GNA1',
      clientId: '7n81v27crlfv6r0uh1o07alau0',
    };
  } else if (awsEnvironment === 'etsNext') {
    return {
      region: awsRegion,
      poolId: 'us-east-1_cVBkTFB0Y',
      clientId: '21jaur22dqclhu701ook99tgq2',
    };
  } else if (awsEnvironment === 'etsNext-Sit') {
    return {
      region: awsRegion,
      poolId: 'us-east-1_6LLGz16gW',
      clientId: '2ont6lpvfuk90fos909jjdlahl',
    };
  } else {
    throw new Error('Invalid environment supplied. No Cognito config found.');
  }
}

export function getAmplifyConfig() {
  const awsEnvironment = getAWSEnvironment();
  const awsRegion = getAWSRegion();
  const cognitoConfig = getCognitoConfig();

  let origin = '';
  if (typeof window !== 'undefined') {
    origin = window.location.origin;
  }

  const domain = origin
    .replace('https://', '')
    .replace('http://', '')
    .split(':')[0];
  const cookieStorageOptions = getAmplifyCookieStorageOptions(domain);
  const oauthDomainByEnvironment = {
    sso: 'sso.ciswired.com',
    dev: 'tk421.auth.us-east-2.amazoncognito.com',
    etsNext: 'us-east-1cvbktfb0y.auth.us-east-1.amazoncognito.com',
    'etsNext-Sit': 'us-east-16llgz16gw.auth.us-east-1.amazoncognito.com',
  };
  const oauthDomain = oauthDomainByEnvironment[awsEnvironment];

  if (!oauthDomain) {
    throw new Error('Invalid environment supplied. No Amplify config found.');
  }

  const cognitoAuthConfig = {
    userPoolId: cognitoConfig.poolId,
    userPoolClientId: cognitoConfig.clientId,
    loginWith: {
      oauth: {
        domain: oauthDomain,
        scopes: ['email', 'openid', 'profile', 'aws.cognito.signin.user.admin'],
        redirectSignIn: [origin ? `${origin}/sso-finish` : '/sso-finish'],
        redirectSignOut: [origin],
        responseType: 'token',
      },
    },
    authenticationFlowType: activeAuthFlowType,
  };

  // Preserve the existing FIPS override for etsNext environments.
  if (IS_ETS_NEXT && !IS_LOCALHOST) {
    cognitoAuthConfig.userPoolEndpoint = `https://cognito-idp-fips.${awsRegion}.amazonaws.com`;
  }

  return {
    amplifyConfig: {
      Auth: {
        Cognito: cognitoAuthConfig,
      },
      ssr: true,
    },
    cookieStorageOptions,
  };
}

function configureCookieStorage(cookieStorageOptions) {
  if (typeof window === 'undefined') {
    return;
  }

  const cookieStorageKey = JSON.stringify(cookieStorageOptions);
  if (cookieStorageKey === lastCookieStorageKey) {
    return;
  }

  cognitoUserPoolsTokenProvider.setKeyValueStorage(
    new CookieStorage(cookieStorageOptions)
  );
  lastCookieStorageKey = cookieStorageKey;
}

export function configureAmplify() {
  const { amplifyConfig, cookieStorageOptions } = getAmplifyConfig();

  // Jest sets JEST_WORKER_ID. Avoid real Amplify.configure/OAuth setup in jsdom.
  if (typeof process !== 'undefined' && process.env.JEST_WORKER_ID != null) {
    return amplifyConfig;
  }

  const { ssr = true, ...resourceConfig } = amplifyConfig;
  const amplifyConfigKey = JSON.stringify(resourceConfig);
  if (amplifyConfigKey !== lastAmplifyConfigKey) {
    // Use aws-amplify's configure (not @aws-amplify/core) so Cognito auth is registered.
    Amplify.configure(resourceConfig, { ssr });
    lastAmplifyConfigKey = amplifyConfigKey;
  }

  configureCookieStorage(cookieStorageOptions);

  return amplifyConfig;
}

// Temporarily switch authentication flow type (e.g., for NEW_PASSWORD_REQUIRED)
export function switchAuthFlowType(flowType) {
  if (!flowType || flowType === activeAuthFlowType) {
    return;
  }

  activeAuthFlowType = flowType;
  configureAmplify();
}

/** Amplify v6 signIn reads options.authFlowType, not authenticationFlowType in config. */
export function getAuthFlowTypeForSignIn() {
  if (activeAuthFlowType === 'USER_SRP_AUTH') {
    return 'USER_SRP_AUTH';
  }
  return 'CUSTOM_WITH_SRP';
}
